import PMBSelect from 'components/PMBSelect'
import { AlertTriangle } from 'react-feather'
import colors from 'Theme/colors'

// Items whose project differs from the transaction project (an empty transaction project counts as different)
export const getProjectMismatch = (packing, project) => {
  const projectId = project?.id ?? project?.value ?? null
  const items = (packing || []).filter((p) => p?.inventoryId)
  return {
    mismatched: items.filter(
      (p) => p?.projectId && String(p.projectId) !== String(projectId),
    ),
    untagged: items.filter((p) => !p?.projectId),
  }
}

export const toProjectOption = (project) => {
  const id = project?.id ?? project?.value ?? null
  return {
    value: id,
    label: id ? (project?.label ?? project?.title) : 'Tanpa project',
    isCurrent: true,
  }
}

// Transaction project vs item projects, shown inside a confirm modal.
// Tagged items keep their project; untagged items always get the transaction
// project (BE tags them on release). canPick=false is info-only.
const ProjectCheck = ({
  project,
  mismatched = [],
  untagged = [],
  canPick = true,
  value,
  onChange,
}) => {
  const currentOption = toProjectOption(project)
  const selected = value ?? currentOption

  // Searchable list so it stays usable with many projects; most-used first
  const counts = new Map()
  mismatched.forEach((p) => {
    const key = String(p.projectId)
    counts.set(key, {
      value: p.projectId,
      label: p.projectLabel || `Project #${p.projectId}`,
      count: (counts.get(key)?.count ?? 0) + 1,
    })
  })
  const options = [
    currentOption,
    ...[...counts.values()].sort((a, b) => b.count - a.count),
  ]

  // Group untagged items by name so hundreds of lots collapse into a few rows
  const untaggedGroups = [
    ...untagged
      .reduce((map, it) => {
        const name = it?.nameItem || it?.itemName || it?.item?.nameItem || '-'
        const unit = it?.unit?.label ?? it?.unit?.value ?? it?.unit ?? ''
        const num = Number(it?.amount)
        // Same id-ID format as "Total Berat"; strings already formatted pass through
        const amount = Number.isFinite(num)
          ? num.toLocaleString('id-ID', { maximumFractionDigits: 2 })
          : (it?.amount ?? '')
        map.set(name, {
          name,
          count: (map.get(name)?.count ?? 0) + 1,
          single: `${amount} ${unit}`.trim(),
        })
        return map
      }, new Map())
      .values(),
  ].sort((a, b) => b.count - a.count)

  return (
    <div
      className="rounded p-3"
      style={{
        backgroundColor: colors.warning[25],
        border: `1px solid ${colors.warning[200]}`,
      }}
      data-testid="project-check"
    >
      <div className="d-flex align-items-center gap-2">
        <AlertTriangle size={16} color={colors.warning[600]} />
        <span className="fw-semibold text-sm">
          {mismatched.length > 0
            ? 'Project barang berbeda'
            : 'Barang tanpa project'}
        </span>
      </div>

      {mismatched.length > 0 && (
        <p className="text-xs mt-1 mb-2" style={{ color: colors.neutral[600] }}>
          {canPick
            ? 'Pilih project transaksi. Project barang tidak berubah.'
            : `${mismatched.length} barang tetap di project masing-masing.`}
        </p>
      )}

      {mismatched.length > 0 && canPick && (
        <PMBSelect
          value={selected}
          onChange={(opt) => opt && onChange?.(opt)}
          options={options}
          isClearable={false}
          placeholder="Cari project"
          formatOptionLabel={(opt) => (
            <div className="d-flex justify-content-between gap-2">
              <span>{opt.label}</span>
              <span className="text-xs text-muted">
                {opt.isCurrent ? 'saat ini' : `${opt.count} barang`}
              </span>
            </div>
          )}
          testId="project-check-select"
        />
      )}

      {untagged.length > 0 && selected?.value && (
        <>
          <p
            className="text-xs mt-2 mb-1"
            style={{ color: colors.neutral[600] }}
            data-testid="project-check-untagged"
          >
            {untagged.length} barang tanpa project akan di-tag{' '}
            <strong>{selected.label}</strong>
          </p>
          <div
            className="rounded px-2 py-1 text-xs bg-white"
            style={{
              border: `1px solid ${colors.neutral[200]}`,
              maxHeight: 120,
              overflowY: 'auto',
            }}
          >
            {untaggedGroups.map((g) => (
              <div
                key={g.name}
                className="d-flex justify-content-between gap-2 py-1"
              >
                <span className="text-truncate">{g.name}</span>
                <span
                  className="flex-shrink-0"
                  style={{ color: colors.neutral[500] }}
                >
                  {g.count > 1 ? `${g.count} barang` : g.single}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default ProjectCheck
